import { describeDomains, noArgsSchema } from "../operation-meta.js";
import { idQueryArgsSchema, projectIDQueryArgsSchema, } from "../projects-input-schemas.js";
import { NotFoundError } from "../../errors.js";
import { canReadProject, scopeToReadableProjects, } from "../project-access.js";
import { assertAdminDataWriteAccess, assertBridgeURLWriteAccess, assertProjectWriteAccess, assertStreamTakerWriteAccess, } from "../projects-helpers.js";
export function createStreamingQueries({ defineProjectQuery, projectZql, }) {
    return {
        studios: {
            list: defineProjectQuery(noArgsSchema, ({ ctx }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.studios.where("id", "__unauthorized__");
                }
                return projectZql.studios
                    .where("deletedAt", "IS", null)
                    .orderBy("name", "asc");
            }),
            byID: defineProjectQuery(idQueryArgsSchema, ({ args, ctx }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.studios.where("id", "__unauthorized__").one();
                }
                return projectZql.studios.where("id", args.id).one();
            }),
        },
        projectAwsResourceLinks: {
            list: defineProjectQuery(noArgsSchema, ({ ctx }) => {
                return scopeToReadableProjects(ctx, projectZql.projectAwsResourceLinks)
                    .orderBy("resourceType", "asc")
                    .orderBy("resourceName", "asc");
            }),
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                if (!canReadProject(ctx, args.projectID)) {
                    return projectZql.projectAwsResourceLinks.where("id", "__unauthorized__");
                }
                return projectZql.projectAwsResourceLinks
                    .where("projectID", args.projectID)
                    .orderBy("resourceType", "asc")
                    .orderBy("resourceName", "asc");
            }),
        },
        streamAudioFeeds: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                if (!canReadProject(ctx, args.projectID)) {
                    return projectZql.streamAudioFeeds.where("id", "__unauthorized__");
                }
                return projectZql.streamAudioFeeds
                    .where("projectID", args.projectID)
                    .orderBy("sortOrder", "asc")
                    .orderBy("name", "asc");
            }),
        },
        streamVideoFeeds: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                if (!canReadProject(ctx, args.projectID)) {
                    return projectZql.streamVideoFeeds.where("id", "__unauthorized__");
                }
                return projectZql.streamVideoFeeds
                    .where("projectID", args.projectID)
                    .orderBy("sortOrder", "asc")
                    .orderBy("name", "asc");
            }),
        },
        streamSignals: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                if (!canReadProject(ctx, args.projectID)) {
                    return projectZql.streamSignals.where("id", "__unauthorized__");
                }
                return projectZql.streamSignals
                    .where("projectID", args.projectID)
                    .related("videoFeed")
                    .related("audioFeedLinks", (q) => q.related("audioFeed").orderBy("sortOrder", "asc"))
                    .orderBy("sortOrder", "asc");
            }),
        },
        streamTakers: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                if (!canReadProject(ctx, args.projectID)) {
                    return projectZql.streamTakers.where("id", "__unauthorized__");
                }
                return projectZql.streamTakers
                    .where("projectID", args.projectID)
                    .related("signal", (q) => q
                    .related("videoFeed")
                    .related("audioFeedLinks", (q2) => q2.related("audioFeed").orderBy("sortOrder", "asc")))
                    .related("contacts", (q) => q.related("person"))
                    .related("audioPairTests")
                    .orderBy("name", "asc");
            }),
        },
    };
}
/**
 * Loads an existing row of a project-scoped table and checks write access on
 * the project it belongs to (never the client's claim about it).
 */
async function assertProjectRowWriteAccess(tx, ctx, query, label, projectID) {
    const row = await tx.run(query.one());
    if (!row || row.projectID !== projectID) {
        throw new NotFoundError(`${label} not found`);
    }
    await assertProjectWriteAccess(tx, ctx, row.projectID);
    return row;
}
/** A referenced row (signal, feed) must belong to the same project. */
async function assertSameProject(tx, query, label, projectID, id) {
    if (!id)
        return;
    const row = await tx.run(query.where("id", id).one());
    if (!row || row.projectID !== projectID) {
        throw new NotFoundError(`${label} not found`);
    }
}
/** An existing child row must belong to the parent the client names. */
async function assertChildOf(tx, query, label, parentField, parentID) {
    const row = await tx.run(query.one());
    if (!row || row[parentField] !== parentID) {
        throw new NotFoundError(`${label} not found`);
    }
}
export function createStreamingMutators({ defineProjectMutator, projectZql, schemas, }) {
    const { createStudioInputSchema, updateStudioInputSchema, studioIDInputSchema, createProjectAwsResourceLinkInputSchema, updateProjectAwsResourceLinkInputSchema, deleteProjectAwsResourceLinkInputSchema, createStreamAudioFeedInputSchema, deleteStreamAudioFeedInputSchema, updateStreamAudioFeedInputSchema, createStreamVideoFeedInputSchema, deleteStreamVideoFeedInputSchema, updateStreamVideoFeedInputSchema, createStreamSignalInputSchema, deleteStreamSignalInputSchema, updateStreamSignalInputSchema, createStreamSignalAudioFeedInputSchema, updateStreamSignalAudioFeedInputSchema, deleteStreamSignalAudioFeedInputSchema, createStreamTakerInputSchema, updateStreamTakerInputSchema, deleteStreamTakerInputSchema, createStreamTakerAudioPairInputSchema, updateStreamTakerAudioPairInputSchema, deleteStreamTakerAudioPairInputSchema, createStreamTakerContactInputSchema, updateStreamTakerContactInputSchema, deleteStreamTakerContactInputSchema, createStreamTakerAudioPairTestInputSchema, updateStreamTakerAudioPairTestInputSchema, deleteStreamTakerAudioPairTestInputSchema, } = schemas;
    return {
        studios: {
            create: defineProjectMutator(createStudioInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                if (args.bridgeURL)
                    assertBridgeURLWriteAccess(ctx);
                await tx.mutate.studios.insert({
                    ...args,
                    deletedAt: null,
                });
            }),
            update: defineProjectMutator(updateStudioInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                if (args.bridgeURL !== undefined && ctx.role !== "admin") {
                    // Forms send the unchanged value along; only a change is refused
                    const studio = await tx.run(projectZql.studios.where("id", args.id).one());
                    if ((studio?.bridgeURL ?? null) !== (args.bridgeURL || null)) {
                        assertBridgeURLWriteAccess(ctx);
                    }
                }
                await tx.mutate.studios.update(args);
            }),
            softDelete: defineProjectMutator(studioIDInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.studios.update({
                    id: args.id,
                    deletedAt: Date.now(),
                });
            }),
        },
        projectAwsResourceLinks: {
            create: defineProjectMutator(createProjectAwsResourceLinkInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectAwsResourceLinks.insert({
                    id: args.id,
                    projectID: args.projectID,
                    resourceType: args.resourceType,
                    resourceID: args.resourceID ?? null,
                    resourceArn: args.resourceArn ?? null,
                    resourceName: args.resourceName ?? null,
                    metadata: args.metadata ?? {},
                });
            }),
            update: defineProjectMutator(updateProjectAwsResourceLinkInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql.projectAwsResourceLinks.where("id", args.id), "AWS resource link", args.projectID);
                await tx.mutate.projectAwsResourceLinks.update({
                    ...args,
                    ...(args.resourceID !== undefined
                        ? { resourceID: args.resourceID ?? null }
                        : {}),
                    ...(args.resourceArn !== undefined
                        ? { resourceArn: args.resourceArn ?? null }
                        : {}),
                    ...(args.resourceName !== undefined
                        ? { resourceName: args.resourceName ?? null }
                        : {}),
                    ...(args.metadata !== undefined
                        ? { metadata: args.metadata ?? {} }
                        : {}),
                });
            }),
            delete: defineProjectMutator(deleteProjectAwsResourceLinkInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql.projectAwsResourceLinks.where("id", args.id), "AWS resource link", args.projectID);
                await tx.mutate.projectAwsResourceLinks.delete({ id: args.id });
            }),
        },
        streamAudioFeeds: {
            create: defineProjectMutator(createStreamAudioFeedInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.streamAudioFeeds.insert({
                    ...args,
                    sortOrder: args.sortOrder ?? 0,
                });
            }),
            delete: defineProjectMutator(deleteStreamAudioFeedInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql.streamAudioFeeds.where("id", args.id), "Audio feed", args.projectID);
                await tx.mutate.streamAudioFeeds.delete({ id: args.id });
            }),
            update: defineProjectMutator(updateStreamAudioFeedInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql.streamAudioFeeds.where("id", args.id), "Audio feed", args.projectID);
                await tx.mutate.streamAudioFeeds.update(args);
            }),
        },
        streamVideoFeeds: {
            create: defineProjectMutator(createStreamVideoFeedInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.streamVideoFeeds.insert({
                    ...args,
                    description: args.description ?? "",
                    sortOrder: args.sortOrder ?? 0,
                });
            }),
            delete: defineProjectMutator(deleteStreamVideoFeedInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql.streamVideoFeeds.where("id", args.id), "Video feed", args.projectID);
                await tx.mutate.streamVideoFeeds.delete({ id: args.id });
            }),
            update: defineProjectMutator(updateStreamVideoFeedInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql.streamVideoFeeds.where("id", args.id), "Video feed", args.projectID);
                await tx.mutate.streamVideoFeeds.update(args);
            }),
        },
        streamSignals: {
            create: defineProjectMutator(createStreamSignalInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await assertSameProject(tx, projectZql.streamVideoFeeds, "Video feed", args.projectID, args.videoFeedID);
                await tx.mutate.streamSignals.insert({
                    ...args,
                    name: args.name ?? "",
                    description: args.description ?? "",
                    videoFeedID: args.videoFeedID ?? null,
                    sortOrder: args.sortOrder ?? 0,
                });
            }),
            delete: defineProjectMutator(deleteStreamSignalInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql.streamSignals.where("id", args.id), "Signal", args.projectID);
                const links = await tx.run(projectZql.streamSignalAudioFeeds.where("signalID", args.id));
                for (const link of links ?? []) {
                    await tx.mutate.streamSignalAudioFeeds.delete({ id: link.id });
                }
                await tx.mutate.streamSignals.delete({ id: args.id });
            }),
            update: defineProjectMutator(updateStreamSignalInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql.streamSignals.where("id", args.id), "Signal", args.projectID);
                await assertSameProject(tx, projectZql.streamVideoFeeds, "Video feed", args.projectID, args.videoFeedID);
                await tx.mutate.streamSignals.update(args);
            }),
        },
        streamSignalAudioFeeds: {
            create: defineProjectMutator(createStreamSignalAudioFeedInputSchema, async ({ tx, ctx, args, }) => {
                const signal = await tx.run(projectZql.streamSignals.where("id", args.signalID).one());
                if (!signal)
                    throw new NotFoundError("Signal not found");
                await assertProjectWriteAccess(tx, ctx, signal.projectID);
                await assertSameProject(tx, projectZql.streamAudioFeeds, "Audio feed", signal.projectID, args.audioFeedID);
                await tx.mutate.streamSignalAudioFeeds.insert({
                    ...args,
                    sortOrder: args.sortOrder ?? 0,
                });
            }),
            update: defineProjectMutator(updateStreamSignalAudioFeedInputSchema, async ({ tx, ctx, args, }) => {
                const signal = await tx.run(projectZql.streamSignals.where("id", args.signalID).one());
                if (!signal)
                    throw new NotFoundError("Signal not found");
                await assertProjectWriteAccess(tx, ctx, signal.projectID);
                await assertChildOf(tx, projectZql.streamSignalAudioFeeds.where("id", args.id), "Signal audio feed", "signalID", args.signalID);
                await tx.mutate.streamSignalAudioFeeds.update(args);
            }),
            delete: defineProjectMutator(deleteStreamSignalAudioFeedInputSchema, async ({ tx, ctx, args, }) => {
                const signal = await tx.run(projectZql.streamSignals.where("id", args.signalID).one());
                if (!signal)
                    throw new NotFoundError("Signal not found");
                await assertProjectWriteAccess(tx, ctx, signal.projectID);
                await assertChildOf(tx, projectZql.streamSignalAudioFeeds.where("id", args.id), "Signal audio feed", "signalID", args.signalID);
                await tx.mutate.streamSignalAudioFeeds.delete({ id: args.id });
            }),
        },
        streamTakers: {
            create: defineProjectMutator(createStreamTakerInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await assertSameProject(tx, projectZql.streamSignals, "Signal", args.projectID, args.signalID);
                await tx.mutate.streamTakers.insert({
                    ...args,
                    deliveryMethod: args.deliveryMethod ?? "",
                    url: args.url ?? "",
                    signalID: args.signalID ?? null,
                    contact: args.contact ?? "",
                    notes: args.notes ?? "",
                    testDate: args.testDate ?? null,
                    tested: args.tested ?? false,
                    preTestDone: args.preTestDone ?? false,
                    onDayTestDone: args.onDayTestDone ?? false,
                });
            }),
            update: defineProjectMutator(updateStreamTakerInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql.streamTakers.where("id", args.id), "Stream taker", args.projectID);
                await assertSameProject(tx, projectZql.streamSignals, "Signal", args.projectID, args.signalID);
                await tx.mutate.streamTakers.update(args);
            }),
            delete: defineProjectMutator(deleteStreamTakerInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql.streamTakers.where("id", args.id), "Stream taker", args.projectID);
                const pairs = await tx.run(projectZql.streamTakerAudioPairs.where("streamTakerID", args.id));
                for (const pair of pairs ?? []) {
                    await tx.mutate.streamTakerAudioPairs.delete({ id: pair.id });
                }
                const contacts = await tx.run(projectZql.streamTakerContacts.where("streamTakerID", args.id));
                for (const c of contacts ?? []) {
                    await tx.mutate.streamTakerContacts.delete({ id: c.id });
                }
                const tests = await tx.run(projectZql.streamTakerAudioPairTests.where("streamTakerID", args.id));
                for (const t of tests ?? []) {
                    await tx.mutate.streamTakerAudioPairTests.delete({ id: t.id });
                }
                await tx.mutate.streamTakers.delete({ id: args.id });
            }),
        },
        streamTakerAudioPairs: {
            create: defineProjectMutator(createStreamTakerAudioPairInputSchema, async ({ tx, ctx, args, }) => {
                const taker = await assertStreamTakerWriteAccess(tx, ctx, args.streamTakerID);
                await assertSameProject(tx, projectZql.streamAudioFeeds, "Audio feed", taker.projectID, args.audioFeedID);
                await tx.mutate.streamTakerAudioPairs.insert({
                    ...args,
                    audioFeedID: args.audioFeedID ?? null,
                });
            }),
            update: defineProjectMutator(updateStreamTakerAudioPairInputSchema, async ({ tx, ctx, args, }) => {
                const taker = await assertStreamTakerWriteAccess(tx, ctx, args.streamTakerID);
                await assertSameProject(tx, projectZql.streamAudioFeeds, "Audio feed", taker.projectID, args.audioFeedID);
                await assertChildOf(tx, projectZql.streamTakerAudioPairs.where("id", args.id), "Audio pair", "streamTakerID", args.streamTakerID);
                await tx.mutate.streamTakerAudioPairs.update(args);
            }),
            delete: defineProjectMutator(deleteStreamTakerAudioPairInputSchema, async ({ tx, ctx, args, }) => {
                await assertStreamTakerWriteAccess(tx, ctx, args.streamTakerID);
                await assertChildOf(tx, projectZql.streamTakerAudioPairs.where("id", args.id), "Audio pair", "streamTakerID", args.streamTakerID);
                await tx.mutate.streamTakerAudioPairs.delete({ id: args.id });
            }),
        },
        streamTakerContacts: {
            create: defineProjectMutator(createStreamTakerContactInputSchema, async ({ tx, ctx, args, }) => {
                await assertStreamTakerWriteAccess(tx, ctx, args.streamTakerID);
                await tx.mutate.streamTakerContacts.insert({
                    ...args,
                    description: args.description ?? "",
                });
            }),
            update: defineProjectMutator(updateStreamTakerContactInputSchema, async ({ tx, ctx, args, }) => {
                await assertStreamTakerWriteAccess(tx, ctx, args.streamTakerID);
                await assertChildOf(tx, projectZql.streamTakerContacts.where("id", args.id), "Contact", "streamTakerID", args.streamTakerID);
                await tx.mutate.streamTakerContacts.update(args);
            }),
            delete: defineProjectMutator(deleteStreamTakerContactInputSchema, async ({ tx, ctx, args, }) => {
                await assertStreamTakerWriteAccess(tx, ctx, args.streamTakerID);
                await assertChildOf(tx, projectZql.streamTakerContacts.where("id", args.id), "Contact", "streamTakerID", args.streamTakerID);
                await tx.mutate.streamTakerContacts.delete({ id: args.id });
            }),
        },
        streamTakerAudioPairTests: {
            create: defineProjectMutator(createStreamTakerAudioPairTestInputSchema, async ({ tx, ctx, args, }) => {
                await assertStreamTakerWriteAccess(tx, ctx, args.streamTakerID);
                await tx.mutate.streamTakerAudioPairTests.insert({
                    ...args,
                    tested: args.tested ?? false,
                    preTestDone: args.preTestDone ?? false,
                    onDayTestDone: args.onDayTestDone ?? false,
                });
            }),
            update: defineProjectMutator(updateStreamTakerAudioPairTestInputSchema, async ({ tx, ctx, args, }) => {
                await assertStreamTakerWriteAccess(tx, ctx, args.streamTakerID);
                await assertChildOf(tx, projectZql.streamTakerAudioPairTests.where("id", args.id), "Audio pair test", "streamTakerID", args.streamTakerID);
                await tx.mutate.streamTakerAudioPairTests.update(args);
            }),
            delete: defineProjectMutator(deleteStreamTakerAudioPairTestInputSchema, async ({ tx, ctx, args, }) => {
                await assertStreamTakerWriteAccess(tx, ctx, args.streamTakerID);
                await assertChildOf(tx, projectZql.streamTakerAudioPairTests.where("id", args.id), "Audio pair test", "streamTakerID", args.streamTakerID);
                await tx.mutate.streamTakerAudioPairTests.delete({ id: args.id });
            }),
        },
    };
}
export const streamingDomainDescriptions = describeDomains({
    studios: "Studios (control rooms) and their equipment setup",
    streamAudioFeeds: "Audio feeds of a project's stream",
    streamVideoFeeds: "Video feeds of a project's stream",
    streamSignals: "Signals (feeds) delivered to stream takers",
    streamSignalAudioFeeds: "Audio feeds carried by a stream signal",
    streamTakers: "Broadcasters and partners taking a stream",
    streamTakerAudioPairs: "Audio pair mapping per stream taker",
    streamTakerContacts: "Contacts of a stream taker",
    streamTakerAudioPairTests: "Audio line-up tests per stream taker",
    projectAwsResourceLinks: "AWS MediaLive/MediaConnect resources linked to a project",
});
